import { useEffect, useState } from "react";
import { motion, useScroll, AnimatePresence } from "framer-motion";
import { useTheme } from "next-themes";
import { Menu, X, Sun, Moon, Phone, MessageCircle, ArrowUp, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV_LINKS, BRAND } from "@/lib/data";
import { useEnquiry } from "@/components/Enquiry";
import { LogoMark } from "@/components/Logo";

export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  return (
    <motion.div style={{ scaleX: scrollYProgress }} className="fixed top-0 left-0 right-0 h-1 bg-primary origin-left z-[60]" data-testid="scroll-progress" />
  );
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const [m, setM] = useState(false);
  useEffect(() => setM(true), []);
  if (!m) return null;
  return (
    <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle"
      className="p-2 rounded-full hover:bg-foreground/5 transition-colors" aria-label="Toggle theme">
      {theme === "dark" ? <Sun className="w-5 h-5 text-gold" /> : <Moon className="w-5 h-5" />}
    </button>
  );
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { open: openEnquiry } = useEnquiry();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <ScrollProgress />
      <motion.nav initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${scrolled ? "glass shadow-sm" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
          <a href="/" data-testid="logo" className="flex items-center gap-2.5 group">
            <LogoMark className="h-10 w-auto group-hover:scale-105 transition-transform" />
            <span className={`leading-none ${scrolled ? "text-foreground" : "text-white"}`}>
              <span className="block font-sans font-bold text-base tracking-[0.12em]">SMART LIVING</span>
              <span className={`block text-[10px] font-sans uppercase tracking-[0.34em] mt-0.5 ${scrolled ? "text-primary" : "text-gold"}`}>Interiors</span>
            </span>
          </a>

          <div className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} data-testid={`nav-${l.label.toLowerCase()}`}
                className={`text-sm font-medium relative after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-0.5 after:bg-primary hover:after:w-full after:transition-all ${scrolled ? "text-foreground/80 hover:text-primary" : "text-white/90 hover:text-white"}`}>
                {l.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <div className={scrolled ? "" : "text-white"}><ThemeToggle /></div>
            <Button onClick={() => openEnquiry({ source: "navbar" })} data-testid="nav-consult"
              className="hidden sm:inline-flex rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-5">
              Free Consultation
            </Button>
            <button className={`lg:hidden p-2 ${scrolled ? "" : "text-white"}`} onClick={() => setOpen(true)} data-testid="mobile-menu-btn" aria-label="Menu">
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-background lg:hidden p-6" data-testid="mobile-menu">
            <div className="flex justify-between items-center h-14">
              <span className="font-display font-bold text-lg">Menu</span>
              <button onClick={() => setOpen(false)} data-testid="mobile-close"><X className="w-7 h-7" /></button>
            </div>
            <div className="flex flex-col gap-1 mt-6">
              {NAV_LINKS.map((l, i) => (
                <motion.a key={l.href} href={l.href} onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                  className="text-2xl font-display py-3 border-b border-border">{l.label}</motion.a>
              ))}
              <Button onClick={() => { setOpen(false); openEnquiry({ source: "mobile" }); }} className="rounded-full mt-6 bg-primary text-primary-foreground">Free Consultation</Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function FloatingActions() {
  const [showTop, setShowTop] = useState(false);
  const { openEstimator } = useEnquiry();
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="fixed right-4 bottom-4 z-50 flex flex-col gap-3 items-end">
      <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noreferrer" data-testid="float-whatsapp"
        className="w-14 h-14 rounded-full bg-[#25D366] shadow-lg flex items-center justify-center text-white hover:scale-110 transition-transform floaty">
        <MessageCircle className="w-7 h-7" />
      </a>
      <a href={`tel:${BRAND.phone}`} data-testid="float-call"
        className="w-14 h-14 rounded-full bg-primary shadow-lg flex items-center justify-center text-white hover:scale-110 transition-transform">
        <Phone className="w-6 h-6" />
      </a>
      <button onClick={openEstimator} data-testid="float-estimator"
        className="w-14 h-14 rounded-full glass border border-gold/40 shadow-lg flex items-center justify-center text-gold hover:scale-110 transition-transform" aria-label="Cost estimator">
        <Calculator className="w-6 h-6" />
      </button>
      <AnimatePresence>
        {showTop && (
          <motion.button initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.5 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="back-to-top"
            className="w-11 h-11 rounded-full bg-foreground text-background shadow-lg flex items-center justify-center hover:scale-110 transition-transform">
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="bg-charcoal text-white/80 pt-20 pb-8 grain relative" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <LogoMark className="h-9 w-auto" />
            <span className="font-sans font-bold text-base tracking-[0.12em] text-white">SMART LIVING</span>
          </div>
          <p className="text-sm text-white/60 leading-relaxed">{BRAND.tagline}. Crafting luxury living experiences across {BRAND.city}.</p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Quick Links</h4>
          <ul className="space-y-2 text-sm">
            {NAV_LINKS.map((l) => <li key={l.href}><a href={l.href} className="hover:text-primary transition-colors">{l.label}</a></li>)}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Services</h4>
          <ul className="space-y-2 text-sm">
            {["Modular Kitchens", "Wardrobes", "TV Units", "False Ceiling", "Office Interiors"].map((s) => <li key={s} className="text-white/60">{s}</li>)}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Contact</h4>
          <p className="text-sm mb-2">{BRAND.address}</p>
          <a href={`tel:${BRAND.phone}`} className="block text-sm hover:text-primary">{BRAND.phoneIntl}</a>
          <a href={`mailto:${BRAND.email}`} className="block text-sm hover:text-primary mt-1">{BRAND.email}</a>
          <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noreferrer" className="block text-sm hover:text-primary mt-1">WhatsApp Us</a>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/50">
        <p>© {new Date().getFullYear()} Smart Living Interiors. All rights reserved.</p>
        <a href="/admin/login" className="hover:text-white/80">Admin</a>
      </div>
    </footer>
  );
}
